// SPDX-License-Identifier: BUSL-1.1

/**
 * Heartbeat staleness chip — renders the age of an agent's last heartbeat
 * with a color band that mirrors the HB-04 watchdog thresholds in
 * `packages/core/src/worker/scheduled.ts` (NocoDB #4568).
 *
 * Server-side, when an agent goes longer than `STALL_SUSPICIOUS_SECONDS`
 * (default 300s) without an activity row, the watchdog emits an
 * `ops_agent_activity` row with `kind='blocker'`. Beyond
 * `STALL_CRITICAL_SECONDS` (default 900s), critical-tier escalations
 * also POST to `STALL_WEBHOOK_URL`.
 *
 * The chip surfaces those same thresholds in the UI so an operator
 * looking at the agent detail page sees the same severity buckets the
 * watchdog uses.
 *
 * Render rules:
 *   - last_heartbeat null  → grey "no heartbeat" (pre-PIO-31 agent or
 *                             newly-registered)
 *   - age < 60s            → green "Xs ago" (live)
 *   - 60s ≤ age < 300s     → green "Xm ago" (within first poll window;
 *                             still healthy because the runner heartbeats
 *                             every 30s)
 *   - 300s ≤ age < 900s    → amber "Xm ago" (suspicious — matches
 *                             watchdog's stall_suspected emission)
 *   - age ≥ 900s           → red   "STALE — Xm ago" (critical — matches
 *                             watchdog's notifyCriticalStalls path)
 *
 * Pure presentation. The agent record carries `lastHeartbeat`; the chip
 * does NO data fetching.
 */

import type * as React from 'react';

interface Props {
  /** ISO timestamp string from `ops_agents.last_heartbeat`, or null. */
  lastHeartbeat: string | null | undefined;
  /**
   * Override for testing or future per-agent thresholds (matches
   * env vars `STALL_SUSPICIOUS_SECONDS` / `STALL_CRITICAL_SECONDS`).
   */
  suspiciousSeconds?: number;
  criticalSeconds?: number;
}

const DEFAULT_SUSPICIOUS_SECONDS = 300;
const DEFAULT_CRITICAL_SECONDS = 900;

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function HeartbeatStalenessChip({
  lastHeartbeat,
  suspiciousSeconds = DEFAULT_SUSPICIOUS_SECONDS,
  criticalSeconds = DEFAULT_CRITICAL_SECONDS,
}: Props): React.ReactElement {
  if (!lastHeartbeat) {
    return (
      <div
        data-testid="heartbeat-staleness-chip"
        data-state="unknown"
        className="inline-flex items-center gap-2 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-400"
        title="Agent has not yet sent a heartbeat. May be pre-PIO-31 code or newly registered."
      >
        <span className="inline-block h-2 w-2 rounded-full bg-neutral-500" />
        <span className="font-medium">heartbeat: unknown</span>
      </div>
    );
  }

  const ageSec = Math.floor((Date.now() - new Date(lastHeartbeat).getTime()) / 1000);

  let state: 'healthy' | 'watch' | 'critical';
  let label: string;
  let dotClass: string;
  let chipClass: string;
  let titleText: string;

  if (ageSec >= criticalSeconds) {
    state = 'critical';
    label = `STALE — ${formatAge(ageSec)}`;
    dotClass = 'bg-red-500';
    chipClass = 'border-red-700 bg-red-950/40 text-red-300';
    titleText = `Critical: no heartbeat in ${formatAge(ageSec)}. HB-04 watchdog has likely fired notifyCriticalStalls.`;
  } else if (ageSec >= suspiciousSeconds) {
    state = 'watch';
    label = formatAge(ageSec);
    dotClass = 'bg-amber-500';
    chipClass = 'border-amber-700 bg-amber-950/30 text-amber-300';
    titleText = `Suspicious: no heartbeat in ${formatAge(ageSec)}. HB-04 watchdog has likely emitted a stall_suspected blocker row.`;
  } else {
    state = 'healthy';
    label = formatAge(ageSec);
    dotClass = 'bg-green-500';
    chipClass = 'border-green-700 bg-green-950/30 text-green-300';
    titleText = `Healthy: last heartbeat ${formatAge(ageSec)}. Runner heartbeat cadence is 30s by default.`;
  }

  return (
    <div
      data-testid="heartbeat-staleness-chip"
      data-state={state}
      className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${chipClass}`}
      title={titleText}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} />
      <span className="font-medium">heartbeat: {label}</span>
    </div>
  );
}
